import { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import "./css/FilterModal.css";

export default function FilterModal({
  isOpen,
  onClose,
  onApply,
  profileSubCategories = [],
  profileWorkAmount = null,
  lang = "en",
}) {
  const [categories, setCategories] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [loading, setLoading] = useState(true);

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;

  // Translations
  const t = {
    en: {
      categories: "Categories",
      searchCategories: "Search Categories",
      costEstimate: "Cost Estimate",
      min: "Min",
      max: "Max",
      apply: "Apply",
      reset: "Reset",
    },
    ar: {
      categories: "الفئات",
      searchCategories: "البحث عن الفئات",
      costEstimate: "تقدير التكلفة",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      apply: "تطبيق",
      reset: "إعادة تعيين",
    },
  }[lang];

  // Fetch categories and sub-categories
  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/sub-category/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const { categories: fetchedCategories = [] } = data.response;
          setCategories(fetchedCategories);
          
          // Initialize expanded categories
          const expanded = {};
          data.response.forEach((cat) => {
            expanded[cat._id] = true;
          });
          setExpandedCategories(expanded);

          // Extract sub_categories from all categories and match with profile
          const allSubCategories = [];
          fetchedCategories.forEach((cat) => {
            if (cat.sub_categories) {
              cat.sub_categories.forEach((subCat) => {
                allSubCategories.push(subCat);
              });
            }
          });

          // Set selected sub-categories from profile (check by default)
          const profileSubCatIds = profileSubCategories.map((sc) =>
            typeof sc === "object" && sc._id ? sc._id : sc.toString()
          );
          
          // Filter to only include sub-categories that exist in categories
          const validSubCatIds = profileSubCatIds.filter((id) =>
            allSubCategories.some((sc) => sc._id === id)
          );
          
          setSelectedSubCategories(validSubCatIds);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen, USER_BASE_URL, VERSION, profileSubCategories]);

  // Initialize work amount from profile
  useEffect(() => {
    if (profileWorkAmount) {
      setMinAmount(profileWorkAmount.min?.toString() || "");
      setMaxAmount(profileWorkAmount.max?.toString() || "");
    }
  }, [profileWorkAmount]);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Toggle sub-category selection
  const toggleSubCategory = (subCategoryId) => {
    setSelectedSubCategories((prev) => {
      if (prev.includes(subCategoryId)) {
        return prev.filter((id) => id !== subCategoryId);
      } else {
        return [...prev, subCategoryId];
      }
    });
  };

  // Remove selected sub-category
  const removeSubCategory = (subCategoryId) => {
    setSelectedSubCategories((prev) => prev.filter((id) => id !== subCategoryId));
  };

  // Get sub-category name
  const getSubCategoryName = (subCategoryId) => {
    for (const cat of categories) {
      const subCat = cat.sub_categories?.find((sc) => sc._id === subCategoryId);
      if (subCat) {
        return `${cat.name} - ${subCat.name}`;
      }
    }
    return "";
  };

  // Filter categories based on search
  const filteredCategories = categories.filter((cat) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    if (cat.name.toLowerCase().includes(query)) return true;
    return cat.sub_categories?.some((sc) =>
      sc.name.toLowerCase().includes(query)
    );
  });

  // Filter sub-categories based on search
  const filterSubCategories = (subCategories) => {
    if (!searchQuery.trim()) return subCategories;
    const query = searchQuery.toLowerCase();
    return subCategories.filter((sc) =>
      sc.name.toLowerCase().includes(query)
    );
  };

  // Handle apply filter
  const handleApply = () => {
    if (selectedSubCategories.length === 0) {
      return;
    }

    const payload = {
      sub_categories: selectedSubCategories,
    };

    if (minAmount && maxAmount) {
      const minNum = parseFloat(minAmount);
      const maxNum = parseFloat(maxAmount);
      if (!isNaN(minNum) && !isNaN(maxNum) && minNum >= 0 && maxNum >= 0) {
        payload.work_amount = {
          min: minNum,
          max: maxNum,
        };
      }
    }

    onApply(payload);
  };

  // Handle reset
  const handleReset = () => {
    // Reset to profile defaults
    const profileSubCatIds = profileSubCategories.map((sc) =>
      typeof sc === "object" && sc._id ? sc._id : sc.toString()
    );
    setSelectedSubCategories(profileSubCatIds);
    
    if (profileWorkAmount) {
      setMinAmount(profileWorkAmount.min?.toString() || "");
      setMaxAmount(profileWorkAmount.max?.toString() || "");
    } else {
      setMinAmount("");
      setMaxAmount("");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="filter-modal-overlay" onClick={onClose}>
      <div className="filter-modal" onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}>
        <div className="filter-modal-header">
          <h2 className="filter-modal-title">{t.categories}</h2>
          <button className="filter-modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="filter-modal-content">
          {loading ? (
            <div className="filter-loading">
              <div className="spinner"></div>
            </div>
          ) : (
            <>
              {/* Selected Categories */}
              {selectedSubCategories.length > 0 && (
                <div className="selected-categories-container">
                  <div className="selected-categories-scroll">
                    {selectedSubCategories.map((subCatId) => (
                      <div key={subCatId} className="selected-category-chip">
                        <span>{getSubCategoryName(subCatId)}</span>
                        <button
                          type="button"
                          onClick={() => removeSubCategory(subCatId)}
                          className="remove-category-btn"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories List - Only show categories that have sub_categories from profile */}
              <div className="filter-categories-list">
                {filteredCategories
                  .filter((category) => {
                    // Only show categories that have at least one sub-category from profile
                    if (!category.sub_categories) return false;
                    const profileSubCatIds = profileSubCategories.map((sc) =>
                      typeof sc === "object" && sc._id ? sc._id : sc.toString()
                    );
                    return category.sub_categories.some((sc) =>
                      profileSubCatIds.includes(sc._id)
                    );
                  })
                  .map((category) => {
                    // Filter sub_categories to only show those from profile
                    const profileSubCatIds = profileSubCategories.map((sc) =>
                      typeof sc === "object" && sc._id ? sc._id : sc.toString()
                    );
                    const profileSubCategoriesInCategory = (category.sub_categories || []).filter(
                      (sc) => profileSubCatIds.includes(sc._id)
                    );

                    return (
                      <div key={category._id} className="filter-category-item">
                        <div
                          className="filter-category-header"
                          onClick={() => toggleCategory(category._id)}
                        >
                          <div className="filter-category-checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={profileSubCategoriesInCategory.some((sc) =>
                                selectedSubCategories.includes(sc._id)
                              )}
                              onChange={() => {}}
                              className="filter-category-checkbox"
                            />
                          </div>
                          <span className="filter-category-name">{category.name}</span>
                          <span className="filter-category-arrow">
                            {expandedCategories[category._id] ? "▲" : "▼"}
                          </span>
                        </div>

                        {expandedCategories[category._id] && (
                          <div className="filter-sub-categories-container">
                            <div className="filter-search-container">
                              <input
                                type="text"
                                placeholder={t.searchCategories}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="filter-search-input"
                              />
                              <i className="bx bx-search"></i>
                            </div>

                            <div className="filter-sub-categories-list">
                              {filterSubCategories(profileSubCategoriesInCategory).map(
                                (subCategory) => {
                                  const isSelected = selectedSubCategories.includes(
                                    subCategory._id
                                  );
                                  return (
                                    <label
                                      key={subCategory._id}
                                      className={`filter-sub-category-item ${
                                        isSelected ? "selected" : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleSubCategory(subCategory._id)}
                                        className="filter-sub-category-checkbox"
                                      />
                                      <span className="filter-sub-category-name">
                                        {subCategory.name}
                                      </span>
                                    </label>
                                  );
                                }
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>

              {/* Cost Estimate */}
              <div className="filter-cost-section">
                <h3 className="filter-cost-title">{t.costEstimate}</h3>
                <div className="filter-cost-inputs">
                  <div className="filter-cost-input-group">
                    <label>{t.min}</label>
                    <input
                      type="number"
                      placeholder={t.min}
                      value={minAmount}
                      onChange={(e) => setMinAmount(e.target.value)}
                      className="filter-cost-input"
                      min="0"
                    />
                  </div>
                  <div className="filter-cost-input-group">
                    <label>{t.max}</label>
                    <input
                      type="number"
                      placeholder={t.max}
                      value={maxAmount}
                      onChange={(e) => setMaxAmount(e.target.value)}
                      className="filter-cost-input"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="filter-modal-footer">
          <button className="filter-reset-btn" onClick={handleReset}>
            {t.reset}
          </button>
          <button
            className="filter-apply-btn"
            onClick={handleApply}
            disabled={selectedSubCategories.length === 0 || loading}
          >
            {t.apply}
          </button>
        </div>
      </div>
    </div>
  );
}
